import { observer } from "mobx-react";
import { Dialog } from "@/components-v2/Dialog";
import { Button, LinkButton } from "@/components-v2/Button";
import { Box, VBox } from "@/components-v2/Box";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { DateInput } from "@/components-v2/DateInput";
import { prepDateInput } from "@/components-v2/dateInputDomain";
import { Select } from "@/components-v2/Select";
import { Tag } from "@/components-v2/Tag";
import { TextInput } from "@/components-v2/TextInput";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { DCCallback, useTranslation } from "@/stores/RootStoreContext";
import { makeCountrySelect } from "@/utils/countries";
import {
  addCondition,
  commitConditionInput,
  conditionDate,
  conditionInput,
  fieldOperators,
  FilterDialogRow,
  FilterDialogState,
  FilterDialogUpdate,
  filterDialogRows,
  LogicalOperator,
  operatorLabel,
  removeCondition,
  resetCondition,
  setConditionDate,
  setConditionField,
  setConditionInput,
  setConditionOperator,
  setLogicalOperator,
} from "./filterDialogDomain";

export type FilterDialogProps<Id extends string> = {
  state: FilterDialogState<Id>;
  update: FilterDialogUpdate<Id>;
  onSubmit: DCCallback;
  onCancel: DCCallback;
};

const LOGICAL_OPERATORS: LogicalOperator[] = ["and", "or"];

const LOGICAL_OPERATOR_LABELS: Record<LogicalOperator, TMessageKey> = {
  and: "filter-dialog.logical-operator.and",
  or: "filter-dialog.logical-operator.or",
};

export const FilterDialog = observer(function FilterDialog<Id extends string>({
  state,
  update,
  onSubmit,
  onCancel,
}: FilterDialogProps<Id>) {
  const { t, lang } = useTranslation();

  const tableColumns: TableColumn<FilterDialogRow<Id>>[] = [
    {
      name: "logicalOperator",
      label: "",
      align: "right",
      notSorted: true,
      getValue: ({ index }) =>
        index === 0 ? (
          { key: "filter-dialog.where" }
        ) : index === 1 ? (
          <Select
            value={state.logicalOperator}
            items={LOGICAL_OPERATORS}
            idFn={(logicalOperator) => logicalOperator}
            labelFn={(logicalOperator) => ({ key: LOGICAL_OPERATOR_LABELS[logicalOperator] })}
            onSelect={(logicalOperator) => [update((s) => setLogicalOperator(s, logicalOperator))]}
          />
        ) : (
          { key: LOGICAL_OPERATOR_LABELS[state.logicalOperator] }
        ),
    },
    {
      name: "field",
      label: "",
      notSorted: true,
      getValue: ({ index, field }) => (
        <Select
          value={field}
          items={state.fields}
          idFn={({ id }) => id}
          labelFn={({ label }) => label}
          onSelect={(selected) => [update((s) => setConditionField(s, index, selected))]}
        />
      ),
    },
    {
      name: "operator",
      label: "",
      notSorted: true,
      getValue: ({ index, field, condition }) => (
        <Select
          value={condition.operator}
          items={fieldOperators(field)}
          idFn={(operator) => operator}
          labelFn={(operator) => operatorLabel(field, operator)}
          onSelect={(operator) => [update((s) => setConditionOperator(s, index, operator))]}
        />
      ),
    },
    {
      name: "value",
      label: "",
      notSorted: true,
      getValue: (row) =>
        row.field.type === "options" ? (
          <Select
            value={row.field.options.find(({ value }) => value === row.condition.value)}
            items={row.field.options}
            idFn={({ value }) => value}
            labelFn={({ label }) => label}
            onSelect={({ value }) => [update((s) => setConditionInput(s, row.index, value, lang))]}
          />
        ) : row.field.type === "country" ? (
          <Select
            {...makeCountrySelect({
              value: row.condition.value || undefined,
              placeholder: t("choose"),
              onSelect: (code) => [update((s) => setConditionInput(s, row.index, code, lang))],
            })}
          />
        ) : row.field.type === "date" ? (
          <DateInput
            {...prepDateInput({
              state: conditionDate(row),
              onInput: (date) => [update((s) => setConditionDate(s, row.index, date))],
              onChange: (date) => [update((s) => setConditionDate(s, row.index, date))],
            })}
            autoSelect
            autofocus={row.index === state.conditions.length - 1}
            messageVariant={"tooltip"}
          />
        ) : (
          <TextInput
            value={conditionInput(row, lang)}
            onChange={(event) => [
              update((s) => setConditionInput(s, row.index, event.target.value, lang)),
            ]}
            onBlur={[update((s) => commitConditionInput(s, row.index))]}
            addon={
              row.field.type === "number" && row.field.suffix ? (
                <Box paddingX={"md"}>{row.field.suffix}</Box>
              ) : undefined
            }
            addonPosition={"right"}
            autoSelect
            autofocus={row.index === state.conditions.length - 1}
          />
        ),
    },
  ];

  return (
    <Dialog
      title={{ key: "common.filter" }}
      onSubmit={onSubmit}
      onCancel={onCancel}
      submitLabel={{ key: "use" }}
      padding={"md"}
    >
      <VBox gap={"md"}>
        <DataTable<FilterDialogRow<Id>>
          variant={"IN_CARD"}
          getId={({ index }) => index}
          tableData={filterDialogRows(state)}
          tableColumns={tableColumns}
          rowActions={({ index }) => [
            [
              "icon",
              state.conditions.length === 1
                ? {
                    icon: "arrow-counter-clockwise",
                    tooltip: { key: "clear" },
                    onClick: [update((s) => resetCondition(s, index))],
                  }
                : {
                    icon: "trash",
                    color: "error",
                    tooltip: { key: "remove" },
                    onClick: [update((s) => removeCondition(s, index))],
                  },
            ],
          ]}
        />
        <Box>
          <LinkButton
            icon={"plus"}
            label={{ key: "filter-dialog.add-condition" }}
            onClick={[update(addCondition)]}
          />
        </Box>
      </VBox>
    </Dialog>
  );
});

export type FilterButtonProps = {
  tag?: TMessage | TMessageParam;
  compact?: boolean;
  disabled?: boolean;
  onClick: DCCallback;
};

export function FilterButton({ tag, compact, disabled, onClick }: FilterButtonProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant={"basic"}
      fullWidth={false}
      icon={"funnel"}
      tooltip={{ key: "common.filter" }}
      label={
        !compact || tag ? (
          <Box gap={"sm"} alignItems={"center"}>
            {!compact && t("common.filter")}
            {tag && <Tag compact color={"warning"} className={"h-5"} label={tag} />}
          </Box>
        ) : undefined
      }
      disabled={disabled}
      onClick={onClick}
    />
  );
}
